/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {productUrlBuilder, rebuildPathWithParams} from './url'

/**
 * Returns true when a product is a physical or electronic gift card.
 *
 * @param {Object} product - Product object from search/details responses.
 * @returns {boolean}
 */
export const isGiftCardProduct = (product) =>
    !!(
        product?.c_isGiftCard ||
        product?.c_isEGiftCard ||
        product?.representedProduct?.c_isGiftCard ||
        product?.representedProduct?.c_isEGiftCard
    )

// True when a search hit has enough data to seed QuickView's initial render.
export const isEnrichedSearchHit = (p) => Array.isArray(p?.variants) && p.variants.length > 0

// Canonical size/length orderings for derived swatches (unlisted values: numeric, then as-is).
const SIZE_ORDER = [
    'XXXS',
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'XXXL',
    '1X',
    '2X',
    '3X',
    '4X',
    '5X'
]
const LENGTH_ORDER = ['PETITE', 'SHORT', 'REGULAR', 'LONG', 'TALL', 'EXTRA LONG']

const orderedValueComparator = (orderList) => (a, b) => {
    const ra = orderList.indexOf(String(a.value).toUpperCase())
    const rb = orderList.indexOf(String(b.value).toUpperCase())
    if (ra !== -1 && rb !== -1) return ra - rb
    if (ra !== -1) return -1
    if (rb !== -1) return 1
    const na = Number.parseFloat(a.value)
    const nb = Number.parseFloat(b.value)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
    return 0 // keep original (insertion) order for anything else
}

const VALUE_COMPARATORS = {
    size: orderedValueComparator(SIZE_ORDER),
    length: orderedValueComparator(LENGTH_ORDER)
}

// Build a minimal, ordered variationAttributes from a hit's variants/variationGroups so
// QuickView can render swatches instantly (names/orderable refined by the product call).
export const buildVariationAttributesFromHit = (hit) => {
    if (!hit?.variants?.length) return []
    const byId = {}
    const addValue = (id, value) => {
        if (value == null) return
        if (!byId[id]) {
            byId[id] = {id, name: id.charAt(0).toUpperCase() + id.slice(1), values: []}
        }
        if (!byId[id].values.some((v) => v.value === value)) {
            // Search hits only carry the raw variation-value code, not its locale-translated
            // display name (e.g. fr-CA size labels differ from the "S"/"M"/"L" codes) — leave
            // `name` unset rather than falling back to the code so a swatch never renders a
            // wrong-locale label while the real product call (which has the translated name)
            // is still in flight.
            byId[id].values.push({value, orderable: true})
        }
    }
    hit.variationGroups?.forEach((vg) => addValue('color', vg.variationValues?.color))
    hit.variants.forEach((variant) => {
        Object.entries(variant.variationValues || {}).forEach(([id, value]) => {
            if (id === 'color') return
            addValue(id, value)
        })
    })
    Object.values(byId).forEach((attr) => {
        const comparator = VALUE_COMPARATORS[attr.id]
        if (comparator) attr.values.sort(comparator)
    })
    const order = ['color', 'size', 'length']
    const rank = (id) => (order.indexOf(id) === -1 ? order.length : order.indexOf(id))
    return Object.values(byId).sort((a, b) => rank(a.id) - rank(b.id))
}

/**
 * Get the human-friendly version of the variation values that users have selected.
 * Useful for displaying these values in the UI.
 *
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {Object} values - The variations selected attribute values.
 * @returns {Object} - A key value map of the display name and display value.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ { "id": "color", "name": "Colour", "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ] } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "royal" }
 */
export const getDisplayVariationValues = (variationAttributes, values = {}) => {
    const returnVal = Object.entries(values).reduce((acc, [id, value]) => {
        const attribute = variationAttributes?.find(({id: attributeId}) => attributeId === id)
        const attributeValue = attribute?.values?.find(
            ({value: attributeValue}) => attributeValue === value
        )
        // Fall back to the raw id/value when the selection is not in the variation attributes.
        return {
            ...acc,
            [attribute?.name || id]: attributeValue?.name || value
        }
    }, {})
    return returnVal
}

/**
 * Normalizes data for product sets and product bundles into the same format
 * Useful for operations that apply to both product sets and product bundles
 *
 * @param {Object} product - A product set or product bundle
 * @returns {Object} - returns normalized product if product is a set/bundle, otherwise returns original product
 */
export const normalizeSetBundleProduct = (product) => {
    if (!product?.type.set && !product?.type.bundle) return product
    return {
        ...product,
        childProducts: product?.type.set
            ? product.setProducts.map((child) => {
                  return {product: child, quantity: null}
              })
            : product.bundledProducts
    }
}

/**
 * Creates an array of product items used to update the child variant selections for a product bundle,
 * usually in an updateItemToBasket/updateItemsToBasket shopper baskets API call
 *
 * @param {Object} bundle - A product that is a bundle.
 * @param {Array} childProductSelections - An array of the bundle child products with a selected variant.
 *      Each item in the array should have the product, variant, and quantity.
 * @returns {Array} - an array of bundle child product items that have the updated variant
 */
export const getUpdateBundleChildArray = (bundle, childProductSelections) => {
    const itemsToBeUpdated = []
    bundle?.bundledProductItems?.forEach((bundleChild) => {
        const childSelection = childProductSelections?.find(
            (childProduct) =>
                childProduct.product?.id === bundleChild.productId ||
                childProduct.product?.itemId === bundleChild.itemId
        )

        // only update the item if the selected variant is different then what's in the current bundle
        if (childSelection && childSelection.variant?.productId !== bundleChild.productId) {
            itemsToBeUpdated.push({
                itemId: bundleChild.itemId,
                productId: childSelection.variant.productId,
                quantity: childSelection.quantity ?? bundleChild.quantity
            })
        }
    })
    return itemsToBeUpdated
}

/**
 * This function extract the price information of a given product
 * If a product is a master,
 *  currentPrice: get the lowest price (including promotional prices) among variants
 *  listPrice: get the list price of the variant that has lowest price (including promotional price)
 *  maxPrice: the max price in tieredPrices of variant that has lowest price
 * @param {object} product - product detail object
 * @param {object} opts - options to pass into the function like intl, quantity, and currency
 */
export const getPriceData = (product, opts = {}) => {
    const {quantity = 1} = opts
    const isASet = product?.hitType === 'set' || !!product?.type?.set
    const isMaster = product?.hitType === 'master' || !!product?.type?.master
    const isVariationGroup = product?.hitType === 'variation_group' || product?.type?.variationGroup
    const lowestPriceData = findLowestPrice(product)
    let currentPrice
    let variantWithLowestPrice
    // grab the variant that has the lowest price (including promotional price)
    if (isMaster || isVariationGroup) {
        variantWithLowestPrice = lowestPriceData
        currentPrice = variantWithLowestPrice?.minPrice
    } else {
        currentPrice = lowestPriceData?.minPrice
    }

    // Get list price and sale price from tieredPrices
    const tieredPrices = variantWithLowestPrice?.data?.tieredPrices || product?.tieredPrices || []

    // Find list price (from list price book) and sale price (from sale price book)
    const listPriceTier = tieredPrices.find((tier) => tier.pricebook?.includes('list_price'))
    const salePriceTier = tieredPrices.find((tier) => tier.pricebook?.includes('sale_price'))

    // Use sale price only if it's lower than current price (to preserve promotional prices)
    if (salePriceTier && salePriceTier.price < currentPrice) {
        currentPrice = salePriceTier.price
    }

    // Check for cart item discounted prices - only if there's an actual discount
    const originalPrice = product?.price || product?.basePrice || currentPrice
    const discountedPrice = product?.priceAfterOrderDiscount || product?.priceAfterItemDiscount
    if (discountedPrice && discountedPrice < originalPrice && discountedPrice < currentPrice) {
        currentPrice = discountedPrice
    }

    // Calculate maxTieredPrice for fallback and maxPrice
    const maxTieredPrice = tieredPrices?.length
        ? Math.max(...tieredPrices.map((item) => item.price))
        : undefined

    // Use list price as the struck-through price
    let listPrice = listPriceTier?.price

    // Fallback to original logic if tiered prices don't have the expected structure
    if (!listPrice) {
        listPrice = tieredPrices.find((tier) => tier.price === maxTieredPrice)?.price

        if (discountedPrice && discountedPrice < originalPrice) {
            listPrice = originalPrice
        }
    }

    // if a product has tieredPrices, get the tiered that has the higher closest quantity to current quantity
    const filteredTiered = tieredPrices.filter((tiered) => tiered.quantity <= quantity)
    const closestTieredPrice =
        filteredTiered.length &&
        filteredTiered.reduce((prev, curr) => {
            return Math.abs(curr.quantity - quantity) < Math.abs(prev.quantity - quantity)
                ? curr
                : prev
        })
    return {
        currentPrice,
        listPrice,
        pricePerUnit: salePriceTier
            ? salePriceTier.price
            : discountedPrice && discountedPrice < originalPrice
            ? discountedPrice
            : product?.pricePerUnit || currentPrice,
        isOnSale: currentPrice < listPrice,
        isASet,
        isMaster,
        // For a product, set price is the lowest price of its children, so the price should be considered a range
        // For a master product, when it has more than 2 variants, we use the lowest priced variant, so it is  considered a range price
        //      but for master that has one variant, it is not considered range
        isRange: (isMaster && product?.variants?.length > 1) || isASet || false,
        // priceMax is for product set
        tieredPrice: closestTieredPrice?.price,
        maxPrice: product?.priceMax || maxTieredPrice,
        newArrivalDate: variantWithLowestPrice?.data?.c_newArrivalDate || product?.c_newArrivalDate || product?.representedProduct?.c_newArrivalDate,
        hasPromotion:
            !!(discountedPrice && discountedPrice < originalPrice) ||
            !!(salePriceTier && listPriceTier && salePriceTier.price < listPriceTier.price)
    }
}

/**
 * Filters the provided product `ImageGroups` array with the optional filters.
 *
 * @param {ImageGroup[]} imageGroups
 * @param {object} filters
 * @param {string} filters.viewType
 * @param {object} filters.variationValues
 *
 * @returns {ImageGroup[]} filteredImageGroups
 */
export const filterImageGroups = (imageGroups = [], filters) => {
    if (!filters) {
        throw new Error(`Missing required "filters" argument`)
    }

    const {viewType, variationValues = {}} = filters
    // NOTE: For future feature enhancement compatibility we use an object for the `variationValues` as
    // we might want to filter on more than the one attribute id/value pair.
    const [selectedAttributeId, selectedAttributeValue] = Object.entries(variationValues)[0] || []

    // Filters
    const typeFilter = viewType ? (group) => group.viewType === viewType : () => true
    const attributeFilter =
        !!selectedAttributeId && !!selectedAttributeValue
            ? ({variationAttributes = []}) =>
                  variationAttributes.some(
                      ({id, values}) =>
                          id === selectedAttributeId &&
                          !!values.find(({value}) => value === selectedAttributeValue)
                  )
            : () => true

    return imageGroups?.filter(typeFilter)?.filter(attributeFilter)
}

/**
 * Provided a product this function will return the variation attibutes decorated with
 * `href` and `swatch` image for the given attribute values. This allows easier access
 * when creating components that commonly use this information.
 *
 * @param {Product} product
 * @param {object} opts
 * @param {string} opts.swatchViewType - The `viewTypeId` for the swatch image. Defaults to `swatch`.
 *
 * @returns {VariationAttributes[]} decoratedVariationAttributes
 */
export const getDecoratedVariationAttributes = (product, opts = {}) => {
    const swatchViewType = opts.swatchViewType || 'swatch'

    return product?.variationAttributes?.map((variationAttribute) => ({
        ...variationAttribute,
        values: variationAttribute?.values?.map((value) => {
            const variationValues = {[variationAttribute.id]: value.value}

            return {
                ...value,
                swatch: filterImageGroups(product.imageGroups, {
                    viewType: swatchViewType,
                    variationValues
                })?.[0]?.images[0],
                href: rebuildPathWithParams(productUrlBuilder(product), variationValues)
            }
        })
    }))
}

/**
 * Find the lowest price of a product, across all of its price books and promotions
 * @param {Object} product - product data from the API
 * @returns {Object|undefined} the lowest price for the given product. If it's a promotional price, the promotion will also be returned.
 */
export const findLowestPrice = (product) => {
    if (!product) return

    // Look at all of the variants, only if it's a master product.
    // i.e. when a shopper has narrowed down to a variant, do not look into other variants
    const isMaster = product?.hitType === 'master' || !!product.type?.master
    const isVariationGroup = product?.hitType === 'variation_group' || product?.type?.variationGroup
    if (isMaster && !product.variants) {
        console.warn(
            'Expecting `product.variants` to exist. For more accuracy, please tweak your API request to ask for the variants details.'
        )
    }
    const array =
        (isMaster && product.variants) || (isVariationGroup && product.variants)
            ? product.variants
            : [product]
    //need to check the case if it is variant itself not group, if needed the promotions could be taken from product.productPromotions

    const res = array.reduce(
        (prev, data) => {
            // if the current variant does not have promotions, fallback to product.productPromotions
            const promotions = data.productPromotions || product.productPromotions || []
            const [smallestPromotionalPrice, promo] = getSmallestValByProperty(
                promotions,
                'promotionalPrice'
            )

            let salePrice = data.price
            let promotion = null
            if (smallestPromotionalPrice && smallestPromotionalPrice < data.price) {
                salePrice = smallestPromotionalPrice
                promotion = promo
            }

            return salePrice < prev.minPrice ? {minPrice: salePrice, promotion, data} : prev
        },
        {minPrice: Infinity, promotion: null, data: null}
    )

    return {
        ...res,
        // when minPrice is infinity, meaning there is no variant with lowest price found, we rest the value to 0 as minPrice to be able to use this value on UI
        minPrice: res.minPrice === Infinity ? 0 : res.minPrice
    }
}

/**
 * @private
 * Find the smallest value by key from a given array
 * @returns {Array} an array of such smallest value and the item containing this value
 * @example
 * const [value, itemContainingValue] = getSmallestValByProperty(array, key)
 */
const getSmallestValByProperty = (arr, key) => {
    if (!arr || !arr.length) return []
    if (!key) {
        throw new Error('Please specify a key.')
    }
    const filtered = arr.filter((item) => {
        const value = item[key]
        return value === 0 || value === '0' ? true : Number(value)
    })
    if (filtered.length === 0) return []

    return filtered.reduce(
        (prev, item) => {
            const value = item[key]
            const [prevValue] = prev
            return value < prevValue ? [value, item] : prev
        },
        [Infinity, null]
    )
}

/**
 * Extracts the numeric `_altN` index from a product image URL/filename
 * (e.g. `100102968_4DD_alt12_1920x2880.jpg` → 12).
 *
 * @param {Object} image - SCAPI image object with `link` / `disBaseLink`.
 * @returns {number|null} Parsed alt index, or null when the URL has no `_altN` segment.
 */
export const getImageAltIndex = (image) => {
    const url = image?.disBaseLink || image?.link || ''
    const match = String(url).match(/_alt(\d+)/i)
    return match ? parseInt(match[1], 10) : null
}

/**
 * Sorts product images by numeric `_altN` in the filename so catalog/feed order
 * does not matter (alt1 → alt2 → … → alt11 → alt12). Images without `_altN`
 * keep their relative order at the start (typical primary/main shot).
 *
 * @param {Array} images - SCAPI image objects.
 * @returns {Array} New array sorted by alt index (stable for ties / missing alts).
 */
export const sortImagesByAltIndex = (images) => {
    if (!Array.isArray(images) || images.length < 2) return images || []

    return images
        .map((image, index) => ({image, index, altIndex: getImageAltIndex(image)}))
        .sort((a, b) => {
            if (a.altIndex == null && b.altIndex != null) return -1
            if (a.altIndex != null && b.altIndex == null) return 1
            if (a.altIndex !== b.altIndex) return a.altIndex - b.altIndex
            return a.index - b.index
        })
        .map(({image}) => image)
}

/**
 * Inserts video items into a base image list at the given 1-indexed positions.
 * Each `positions[i]` is the target slot for `videoItems[i]` in the final sequence;
 * remaining slots are filled with the base images in their original order.
 *
 * @param {Array} baseImages - The ordered list of images to merge videos into.
 * @param {Array} videoItems - Video items to insert (e.g. {isVideo: true, src, alt}).
 * @param {number[]} positions - 1-indexed target slots, aligned with `videoItems`.
 * @returns {Array} The merged sequence of images and videos.
 */
export const insertVideosAtPositions = (baseImages, videoItems, positions) => {
    if (!videoItems?.length || !baseImages?.length) return baseImages

    const totalLength = baseImages.length + videoItems.length
    const result = new Array(totalLength)
    positions.forEach((pos, i) => {
        if (videoItems[i] && pos >= 1 && pos <= totalLength) {
            result[pos - 1] = videoItems[i]
        }
    })
    let imgIdx = 0
    for (let i = 0; i < totalLength; i++) {
        if (!result[i]) result[i] = baseImages[imgIdx++]
    }
    return result.filter(Boolean)
}
